import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ShieldCheck, Check, Star, Sparkles, ArrowLeft, ArrowRight, Factory } from "lucide-react";
import { toast } from "sonner";

export default function NormaGate() {
  const { reload, logout } = useAuth();
  const [paso, setPaso] = useState(1); // 1 = norma, 2 = sector
  const [normas, setNormas] = useState([]);
  const [sectores, setSectores] = useState([]);
  const [sel, setSel] = useState([]); // orden de selección; sel[0] = principal
  const [sector, setSector] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/normas").then((r) => setNormas(r.data)).catch(() => {});
    api.get("/registro/sectores").then((r) => setSectores(r.data)).catch(() => {});
  }, []);

  const toggle = (codigo) =>
    setSel((prev) => (prev.includes(codigo) ? prev.filter((c) => c !== codigo) : [...prev, codigo]));

  const hacerPrincipal = (codigo, e) => {
    e.stopPropagation();
    setSel((prev) => [codigo, ...prev.filter((c) => c !== codigo)]);
  };

  const irASector = () => {
    if (sel.length === 0) return toast.error("Selecciona al menos una norma");
    setPaso(2);
  };

  const confirmar = async () => {
    if (sel.length === 0) return toast.error("Selecciona al menos una norma");
    if (!sector) return toast.error("Selecciona tu sector");
    setSaving(true);
    try {
      await api.post("/cuenta/normas", { normas: sel, principal: sel[0], sector });
      toast.success(sel.length > 1 ? "¡Doble certificación activada!" : "¡Todo listo para empezar!");
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo guardar la selección");
      setSaving(false);
    }
  };

  const principalNombre = normas.find((n) => n.codigo === sel[0])?.nombre_corto || sel[0];

  return (
    <div className="min-h-screen bg-[#0E2A47] flex items-center justify-center px-4 py-10" data-testid="norma-gate">
      <div className="w-full max-w-3xl">
        <div className="flex items-center gap-2 mb-8">
          <ShieldCheck className="text-[#FF5625]" size={28} />
          <span className="font-black text-xl tracking-tighter text-white">AuditReady<span className="text-[#FF5625]">.</span></span>
        </div>

        {/* Indicador de pasos */}
        <div className="flex items-center gap-2 mb-3" data-testid="gate-steps">
          <span className={`h-1.5 w-10 rounded-full transition-all ${paso >= 1 ? "bg-[#FF5625]" : "bg-white/20"}`} />
          <span className={`h-1.5 w-10 rounded-full transition-all ${paso >= 2 ? "bg-[#FF5625]" : "bg-white/20"}`} />
          <span className="text-xs text-white/40 ml-2">Paso {paso} de 2</span>
        </div>

        {paso === 1 ? (
          <>
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-[#FF5625] mb-3">
              <Sparkles size={14} /> Elige tu norma
            </div>
            <h1 className="text-3xl sm:text-4xl font-black tracking-tighter text-white leading-tight">
              Elige tu norma para empezar
            </h1>
            <p className="mt-3 text-white/60 max-w-xl">
              Es la lente con la que evaluaremos tu empresa. Puedes elegir <b className="text-white">más de una</b> si
              persigues doble certificación (p. ej. IFS + BRCGS). Tu evidencia documental se comparte entre normas.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-8">
              {normas.map((n) => {
                const activo = sel.includes(n.codigo);
                const esPrincipal = sel[0] === n.codigo;
                return (
                  <button
                    key={n.codigo}
                    onClick={() => toggle(n.codigo)}
                    data-testid={`gate-norma-${n.codigo}`}
                    className={`text-left rounded-xl p-5 border-2 transition-all ${
                      activo ? "bg-white border-[#FF5625] shadow-lg" : "bg-white/5 border-white/10 hover:border-white/30"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className={`font-bold tracking-tight ${activo ? "text-[#0E2A47]" : "text-white"}`}>
                        {n.nombre_corto || n.nombre}
                      </div>
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${
                        activo ? "bg-[#FF5625] text-white" : "border border-white/30"
                      }`}>
                        {activo && <Check size={14} />}
                      </div>
                    </div>
                    <div className={`text-xs mt-2 leading-snug ${activo ? "text-[#0E2A47]/60" : "text-white/50"}`}>
                      {n.descripcion_corta}
                    </div>
                    {activo && (
                      esPrincipal ? (
                        <Badge className="mt-3 bg-[#0E2A47] hover:bg-[#0E2A47]" data-testid={`gate-principal-${n.codigo}`}>
                          <Star size={11} className="mr-1" /> Norma principal
                        </Badge>
                      ) : (
                        <span
                          role="button"
                          onClick={(e) => hacerPrincipal(n.codigo, e)}
                          data-testid={`gate-hacer-principal-${n.codigo}`}
                          className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-[#FF5625] hover:underline"
                        >
                          <Star size={11} /> Hacer principal
                        </span>
                      )
                    )}
                  </button>
                );
              })}
            </div>

            <div className="flex flex-wrap items-center gap-4 mt-8">
              <Button
                onClick={irASector}
                disabled={sel.length === 0}
                data-testid="gate-continuar"
                className="bg-[#FF5625] hover:bg-[#E0451A] text-white font-semibold px-8"
              >
                Continuar <ArrowRight size={16} className="ml-2" />
              </Button>
              <span className="text-xs text-white/40">
                {sel.length > 0 ? `Principal: ${principalNombre}` : "Selecciona una o varias normas"}
              </span>
              <button
                onClick={() => { logout(); }}
                data-testid="gate-logout"
                className="ml-auto text-xs text-white/40 hover:text-white/70"
              >
                Cerrar sesión
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-[#FF5625] mb-3">
              <Factory size={14} /> Tu sector
            </div>
            <h1 className="text-3xl sm:text-4xl font-black tracking-tighter text-white leading-tight">
              ¿A qué sector perteneces?
            </h1>
            <p className="mt-3 text-white/60 max-w-xl">
              Esto ajusta <b className="text-white">cómo la IA lee e interpreta tus documentos</b>: aplicará la doctrina
              y los criterios propios de tu sector al clasificar y evaluar tus evidencias.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-8">
              {sectores.map((s) => {
                const activo = sector === s.value;
                return (
                  <button
                    key={s.value}
                    onClick={() => setSector(s.value)}
                    data-testid={`gate-sector-${s.value}`}
                    className={`text-left rounded-xl px-5 py-4 border-2 transition-all flex items-center justify-between gap-2 ${
                      activo ? "bg-white border-[#FF5625] shadow-lg" : "bg-white/5 border-white/10 hover:border-white/30"
                    }`}
                  >
                    <span className={`font-semibold tracking-tight ${activo ? "text-[#0E2A47]" : "text-white"}`}>
                      {s.label}
                    </span>
                    <span className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${
                      activo ? "bg-[#FF5625] text-white" : "border border-white/30"
                    }`}>
                      {activo && <Check size={14} />}
                    </span>
                  </button>
                );
              })}
            </div>

            <div className="flex flex-wrap items-center gap-4 mt-8">
              <button
                onClick={() => setPaso(1)}
                data-testid="gate-atras"
                className="inline-flex items-center gap-1 text-sm font-semibold text-white/60 hover:text-white"
              >
                <ArrowLeft size={16} /> Atrás
              </button>
              <Button
                onClick={confirmar}
                disabled={saving || !sector}
                data-testid="gate-confirmar"
                className="bg-[#FF5625] hover:bg-[#E0451A] text-white font-semibold px-8"
              >
                {saving ? "Guardando…" : sel.length > 1 ? `Empezar con ${sel.length} normas` : "Empezar"}
              </Button>
              <span className="text-xs text-white/40">
                Norma principal: {principalNombre}
              </span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
